/**
 * Synthesizercontext
 * application-wideSynthesizerprovide access
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { OscillatorSynth, createDefaultOscillatorSynth, OscillatorWaveform } from '../synth/OscillatorSynth';
import { ADSREnvelope } from '../synth/Synthesizer';

interface SynthContextValue {
  synth: OscillatorSynth | null;
  isInitialized: boolean;
  initialize: () => Promise<void>;
  playNote: (pitch: number, velocity?: number, duration?: number) => void;
  noteOn: (pitch: number, velocity?: number) => string;
  noteOff: (noteId: string) => void;
  setVolume: (volume: number) => void;
  setWaveform: (waveform: OscillatorWaveform) => void;
  setEnvelope: (envelope: Partial<ADSREnvelope>) => void;
  getVolume: () => number;
  getWaveform: () => OscillatorWaveform;
  getEnvelope: () => ADSREnvelope;
}

const SynthContext = createContext<SynthContextValue | null>(null);

export const useSynth = (): SynthContextValue => {
  const context = useContext(SynthContext);
  if (!context) {
    throw new Error('useSynth must be used within a SynthProvider');
  }
  return context;
};

interface SynthProviderProps {
  children: React.ReactNode;
}

export const SynthProvider: React.FC<SynthProviderProps> = ({ children }) => {
  const synthRef = useRef<OscillatorSynth | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize
  const initialize = useCallback(async () => {
    if (synthRef.current) {
      return;
    }

    const synth = createDefaultOscillatorSynth();
    await synth.initialize();
    synthRef.current = synth;
    setIsInitialized(true);
  }, []);

 // NotePlay（specifiedtimeafterautomaticallyStop）
  const playNote = useCallback(async (pitch: number, velocity: number = 100, duration: number = 0.3) => {
    if (!synthRef.current) {
      await initialize();
    }

    if (!synthRef.current) {
      console.warn('Synth not initialized');
      return;
    }

    const noteId = synthRef.current.noteOn(pitch, velocity);

    setTimeout(() => {
      if (synthRef.current) {
        synthRef.current.noteOff(noteId);
      }
    }, duration * 1000);
  }, [initialize]);

  // Note on
  const noteOn = useCallback((pitch: number, velocity: number = 100): string => {
    if (!synthRef.current) {
      console.warn('Synth not initialized');
      return '';
    }
    return synthRef.current.noteOn(pitch, velocity);
  }, []);

  // Note off
  const noteOff = useCallback((noteId: string) => {
    if (synthRef.current) {
      synthRef.current.noteOff(noteId);
    }
  }, []);

  // VolumeSettings
  const setVolume = useCallback((volume: number) => {
    if (synthRef.current) {
      synthRef.current.setVolume(volume);
    }
  }, []);

  // WaveformSettings
  const setWaveform = useCallback((waveform: OscillatorWaveform) => {
    if (synthRef.current) {
      synthRef.current.setWaveform(waveform);
    }
  }, []);

  // EnvelopeSettings
  const setEnvelope = useCallback((envelope: Partial<ADSREnvelope>) => {
    if (synthRef.current) {
      synthRef.current.setEnvelope(envelope);
    }
  }, []);

  // VolumeGet
  const getVolume = useCallback((): number => {
    return synthRef.current?.getVolume() ?? 0.5;
  }, []);

  // WaveformGet
  const getWaveform = useCallback((): OscillatorWaveform => {
    return synthRef.current?.getWaveform() ?? 'sawtooth';
  }, []);

  // EnvelopeGet
  const getEnvelope = useCallback((): ADSREnvelope => {
    return synthRef.current?.getEnvelope() ?? {
      attack: 0.01,
      decay: 0.1,
      sustain: 0.7,
      release: 0.2,
    };
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (synthRef.current) {
        synthRef.current.dispose();
        synthRef.current = null;
      }
    };
  }, []);

  const value: SynthContextValue = {
    synth: synthRef.current,
    isInitialized,
    initialize,
    playNote,
    noteOn,
    noteOff,
    setVolume,
    setWaveform,
    setEnvelope,
    getVolume,
    getWaveform,
    getEnvelope,
  };

  return <SynthContext.Provider value={value}>{children}</SynthContext.Provider>;
};

export default SynthContext;
